export const getUniqueValues = <T extends string | number | boolean>(
  array: T[],
) => {
  return [...new Set([...array])]
}

export function uniqueIdsReducer<T>(
  array: T[],
  field: T extends T ? keyof T : never,
): string[] {
  const ids = array.reduce((acc: string[], item: T) => {
    const value = item[field] as string | string[]

    if (value) acc.push(...(Array.isArray(value) ? value : [value]))

    return acc
  }, [])

  return getUniqueValues(ids)
}

export function uniqueIdsReducers(
  array: {
    array: unknown[]
    field: string
  }[],
): string[] {
  const ids = array
    // @ts-expect-error - array and field types are dynamic
    .map(({ array, field }) => uniqueIdsReducer(array, field))
    .reduce((acc: string[], items) => {
      acc.push(...getUniqueValues(items))

      return acc
    }, [])

  return getUniqueValues(ids)
}

export function generate(length: number): string[] {
  return Array(length).fill(0)
}

/**
 * Helper type that makes specific properties required and non-nullable
 */
type WithRequired<T, K extends keyof T> = T & {
  [P in K]-?: NonNullable<T[P]>
}

/**
 * Filter array items where specific property(ies) are defined, and narrow the type to make them required.
 * This works around TypeScript's limitation where filter() doesn't narrow object properties from optional to required.
 *
 * @example
 * // Single property
 * type Item = { id: number; batchId?: string }
 * const items: Item[] = [{ id: 1, batchId: 'a' }, { id: 2 }]
 * const filtered = filterByRequired(items, 'batchId')
 * // Type: Array<{ id: number; batchId: string }> - batchId is now required!
 *
 * @example
 * // Multiple properties
 * type Item = { id: number; batchId?: string; userId?: string }
 * const items: Item[] = [{ id: 1, batchId: 'a', userId: 'u1' }, { id: 2, batchId: 'b' }]
 * const filtered = filterByRequired(items, ['batchId', 'userId'])
 * // Type: Array<{ id: number; batchId: string; userId: string }> - both are required!
 */
export function filterByRequired<T, K extends keyof T>(
  array: T[],
  key: K,
): Array<WithRequired<T, K>>
export function filterByRequired<T, K extends keyof T>(
  array: T[],
  keys: readonly K[],
): Array<WithRequired<T, K>>
export function filterByRequired<T, K extends keyof T>(
  array: T[],
  keyOrKeys: K | readonly K[],
): Array<WithRequired<T, K>> {
  const keys: K[] = Array.isArray(keyOrKeys) ? [...keyOrKeys] : [keyOrKeys]
  return array.filter((item: T) =>
    keys.every((key: K) => item[key] != undefined),
  ) as Array<WithRequired<T, K>>
}
